import { Command } from "cmdk";
import {
  Activity,
  Briefcase,
  Building2,
  Home,
  Radio,
  Search,
  Target,
  TrendingUp,
  User,
} from "lucide-react";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { COLORS, MOTION, RADIUS, SHADOW, SPACE, TYPE } from "../theme/index.js";

export type SearchResult = {
  companies?: Array<{ id: string; name: string; domain: string | null }>;
  contacts?: Array<{ id: string; name: string; company_name?: string | null }>;
  deals?: Array<{ id: string; name: string; stage: string; arr_usd: number | null }>;
};

type CommandPaletteProps = {
  onSearch?: (query: string) => Promise<SearchResult>;
  onOpen?: () => void;
  onClose?: () => void;
};

export function CommandPalette({ onSearch, onOpen, onClose }: CommandPaletteProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult>({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleOpen = useCallback(() => {
    setOpen(true);
    onOpen?.();
  }, [onOpen]);
  const handleClose = useCallback(() => {
    setOpen(false);
    setQuery("");
    setResults({});
    onClose?.();
  }, [onClose]);
  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
      handleClose();
    },
    [navigate, handleClose],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        open ? handleClose() : handleOpen();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, handleOpen, handleClose]);

  useEffect(() => {
    if (!query || query.length < 2 || !onSearch) {
      setResults({});
      return;
    }
    let cancelled = false;
    setLoading(true);
    onSearch(query)
      .then((r) => {
        if (!cancelled) {
          setResults(r);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query, onSearch]);

  if (!open) return null;

  return (
    <>
      <div
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 90 }}
        onClick={handleClose}
        onKeyDown={(e) => {
          if (e.key === "Escape") handleClose();
        }}
        role="button"
        tabIndex={-1}
        aria-label="Close command palette"
      />
      <div
        style={{
          position: "fixed",
          top: "20%",
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          maxWidth: 560,
          zIndex: 91,
          background: COLORS.bg,
          border: `1px solid ${COLORS.border}`,
          borderRadius: RADIUS["2xl"],
          boxShadow: SHADOW.lg,
          overflow: "hidden",
          animation: `fadeIn ${MOTION.base} ${MOTION.ease}`,
        }}
      >
        <Command label="Command palette" shouldFilter={false}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: SPACE[3],
              padding: `${SPACE[4]}px ${SPACE[5]}px`,
              borderBottom: `1px solid ${COLORS.border}`,
            }}
          >
            <Search size={16} color={COLORS.textDim} style={{ flexShrink: 0 }} />
            <Command.Input
              value={query}
              onValueChange={setQuery}
              placeholder="Search companies, contacts, deals..."
              style={{
                flex: 1,
                background: "none",
                border: "none",
                outline: "none",
                color: COLORS.text,
                fontSize: TYPE.size.md,
                fontFamily: TYPE.family.sans,
              }}
              autoFocus
            />
            {loading && <span style={{ fontSize: TYPE.size.xs, color: COLORS.textDim }}>...</span>}
          </div>
          <Command.List style={{ maxHeight: 380, overflowY: "auto", padding: `${SPACE[2]}px 0` }}>
            <Command.Empty
              style={{
                padding: `${SPACE[8]}px`,
                textAlign: "center",
                color: COLORS.textDim,
                fontSize: TYPE.size.sm,
              }}
            >
              No results found.
            </Command.Empty>

            {results.companies && results.companies.length > 0 && (
              <Group heading="Companies">
                {results.companies.map((c) => (
                  <Item
                    key={c.id}
                    icon={<Building2 size={14} />}
                    label={c.name}
                    hint={c.domain ?? undefined}
                    onSelect={() => handleNavigate(`/prospects/companies/${c.id}`)}
                  />
                ))}
              </Group>
            )}
            {results.contacts && results.contacts.length > 0 && (
              <Group heading="Contacts">
                {results.contacts.map((c) => (
                  <Item
                    key={c.id}
                    icon={<User size={14} />}
                    label={c.name}
                    hint={c.company_name ?? undefined}
                    onSelect={() => handleNavigate(`/prospects/${c.id}`)}
                  />
                ))}
              </Group>
            )}
            {results.deals && results.deals.length > 0 && (
              <Group heading="Deals">
                {results.deals.map((d) => (
                  <Item
                    key={d.id}
                    icon={<Briefcase size={14} />}
                    label={d.name}
                    hint={d.stage}
                    onSelect={() => handleClose()}
                  />
                ))}
              </Group>
            )}

            <Group heading="Pages">
              <Item
                icon={<Home size={14} />}
                label="Overview"
                hint="g h"
                onSelect={() => handleNavigate("/")}
              />
              <Item
                icon={<TrendingUp size={14} />}
                label="Revenue"
                hint="g r"
                onSelect={() => handleNavigate("/revenue")}
              />
              <Item
                icon={<Target size={14} />}
                label="Pipeline"
                hint="g p"
                onSelect={() => handleNavigate("/pipeline")}
              />
              <Item
                icon={<Radio size={14} />}
                label="Top of Funnel"
                hint="g t"
                onSelect={() => handleNavigate("/tof")}
              />
              <Item
                icon={<Activity size={14} />}
                label="Agents Health"
                hint="g a"
                onSelect={() => handleNavigate("/agents")}
              />
            </Group>
          </Command.List>
          <div
            style={{
              padding: `${SPACE[2]}px ${SPACE[5]}px`,
              borderTop: `1px solid ${COLORS.border}`,
              display: "flex",
              gap: SPACE[4],
              fontSize: TYPE.size.xs,
              color: COLORS.textDim,
            }}
          >
            <span>↑↓ Navigate</span>
            <span>↵ Select</span>
            <span>Esc Close</span>
          </div>
        </Command>
      </div>
    </>
  );
}

function Group({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <Command.Group style={{ padding: `${SPACE[1]}px 0` }}>
      <div
        style={{
          padding: `${SPACE[1]}px ${SPACE[5]}px`,
          fontSize: TYPE.size.xs,
          fontWeight: 600,
          color: COLORS.textDim,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        {heading}
      </div>
      {children as never}
    </Command.Group>
  );
}

function Item({
  icon,
  label,
  hint,
  onSelect,
}: { icon: ReactNode; label: string; hint?: string; onSelect: () => void }) {
  return (
    <Command.Item
      onSelect={onSelect}
      style={{
        display: "flex",
        alignItems: "center",
        gap: SPACE[3],
        padding: `${SPACE[2]}px ${SPACE[5]}px`,
        cursor: "pointer",
        fontSize: TYPE.size.md,
        color: COLORS.text,
      }}
    >
      <span style={{ color: COLORS.textDim, flexShrink: 0 }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {hint && (
        <span
          style={{
            fontSize: TYPE.size.xs,
            color: COLORS.textDim,
            background: COLORS.surface,
            padding: `2px ${SPACE[2]}px`,
            borderRadius: RADIUS.base,
          }}
        >
          {hint}
        </span>
      )}
    </Command.Item>
  );
}
